import React, { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import FormControl from '@mui/material/FormControl';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import DateComponent from "../../../components/inputs/date/date";
import SelectAutocomplete from "../../../components/inputs/select/autocomplete/select-autocomplete";
import { useParams } from 'react-router-dom';
import { fetchProjectService } from '../../../features/project/service/project-service';
import SelectGroup from '../../../components/inputs/select/grouped/select-group';
import Icons from '../../../components/icons/iconts'; // Assumindo o caminho correto

interface StepContentProps {
  step: number;
  reportParams: {
    lancamentoType: string;
    rubricaId: string;
    startDate: string;
    endDate: string;
  };
  lancamentoTypes: Array<{
    id: string;
    name: string;
    description: string;
  }>;
  rubricas: Array<{
    id: string;
    name: string;
    description: string;
    label: string;
    value: string;
  }>;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onAutocompleteChange: (option: any) => void;
  onDateChange: (field: string, value: string) => void;
  selectedRubrica: any;
  useFullPeriod?: boolean;
  onFullPeriodChange?: (checked: boolean) => void;
}

const StepContent = ({
  step,
  reportParams,
  lancamentoTypes,
  rubricas,
  onInputChange,
  onAutocompleteChange,
  onDateChange,
  selectedRubrica,
  useFullPeriod = false,
  onFullPeriodChange = () => {}
}: StepContentProps) => {
  const { id } = useParams();
  const [ project, setProject ] = useState<any>();
  
  const [isFullPeriodChecked, setIsFullPeriodChecked] = useState(useFullPeriod);

  useEffect(() => {
    setIsFullPeriodChecked(useFullPeriod);
  }, [useFullPeriod]);

  const fetchProject = useCallback(async () => {
    try {
      const response = await fetchProjectService(id as string);
      
      if(response) {
        setProject(response);
      }
    } catch (error) {
      console.log(error);
    }
  }, [id]);

  const handleFullPeriodChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    
    setIsFullPeriodChecked(checked);
    
    onFullPeriodChange(checked);
    
    if (checked && project) {
      onDateChange('startDate', project.initialDate);
      onDateChange('endDate', new Date().toISOString().split('T')[0]);
    }
  };

  useEffect(() => {
    fetchProject();
  }, [id, fetchProject]);

  // Categorizar tipos de lançamento
  const categorizeTypes = () => {
    const receitas = lancamentoTypes.filter(type => 
      ['receita', 'rendimento', 'devolução'].some(keyword => 
        type.name.toLowerCase().includes(keyword)
      )
    );
    
    const despesas = lancamentoTypes.filter(type => 
      ['despesa', 'empréstimo', 'tarifas'].some(keyword => 
        type.name.toLowerCase().includes(keyword.split(' ')[0])
      )
    );
    
    const outros = lancamentoTypes.filter(type => 
      !receitas.includes(type) && !despesas.includes(type) && type.id !== 'both'
    );
    
    const ambos = lancamentoTypes.find(type => type.id === 'both');
    
    return { receitas, despesas, outros, ambos };
  };
  
  switch (step) {
    case 0: // Tipo de Lançamento
      const { receitas, despesas, outros, ambos } = categorizeTypes();
      
      return (
        <Paper elevation={3} sx={{ p: 3, mt: 2, borderRadius: '8px' }}>
          <Typography variant="h6" gutterBottom sx={{ color: '#4aaa9c', fontWeight: 500 }}>
            Selecione o tipo de lançamento
          </Typography>
          
          <FormControl component="fieldset" sx={{ mt: 3, width: '100%' }}>
            <RadioGroup
              name="lancamentoType"
              value={reportParams.lancamentoType}
              onChange={onInputChange}
            >
              {/* Opção Ambos - Destaque especial */}
              {ambos && (
                <Box sx={{ mb: 3 }}>
                  <Paper 
                    elevation={reportParams.lancamentoType === ambos.id ? 3 : 1} 
                    sx={{ 
                      p: 3, 
                      borderRadius: '12px',
                      border: reportParams.lancamentoType === ambos.id ? '2px solid #4aaa9c' : '1px solid #e0e0e0',
                      transition: 'all 0.3s ease',
                      background: reportParams.lancamentoType === ambos.id ? 
                        'linear-gradient(135deg, #f0f9ff 0%, #e0f7fa 100%)' : '#fff',
                      '&:hover': {
                        elevation: 2,
                        borderColor: '#4aaa9c'
                      }
                    }}
                  >
                    <FormControlLabel 
                      value={ambos.id} 
                      control={<Radio color="primary" sx={{ display: 'none' }} />} 
                      label={
                        <Box sx={{ width: '100%', cursor: 'pointer' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Icons 
                              iconName="AccountBalance" 
                              fontSize={24} 
                              color={reportParams.lancamentoType === ambos.id ? '#4aaa9c' : '#666'}
                            />
                            <Typography variant="h6" sx={{ 
                              color: reportParams.lancamentoType === ambos.id ? '#4aaa9c' : '#333',
                              fontWeight: 600,
                              ml: 1
                            }}>
                              {ambos.name}
                            </Typography>
                            <Chip 
                              label="Recomendado" 
                              size="small" 
                              sx={{ 
                                ml: 2, 
                                bgcolor: '#4aaa9c', 
                                color: 'white',
                                fontSize: '0.7rem'
                              }} 
                            />
                          </Box>
                          <Typography variant="body2" sx={{ color: 'text.secondary', ml: 4 }}>
                            {ambos.description}
                          </Typography>
                        </Box>
                      } 
                      sx={{ width: '100%', m: 0 }}
                    />
                  </Paper>
                </Box>
              )}

              {/* Seções organizadas com Box */}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', md: 'row' },
                gap: 3 
              }}>
                {/* Receitas */}
                {receitas.length > 0 && (
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Icons 
                        iconName="TrendingUp" 
                        fontSize={20} 
                        color="#2e7d32"
                      />
                      <Typography variant="subtitle1" sx={{ ml: 1, color: '#2e7d32', fontWeight: 500 }}>
                        Receitas
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {receitas.map((type) => (
                        <Paper 
                          key={type.id}
                          elevation={reportParams.lancamentoType === type.id ? 2 : 0} 
                          sx={{ 
                            p: 2, 
                            borderRadius: '8px',
                            border: reportParams.lancamentoType === type.id ? '2px solid #2e7d32' : '1px solid #e8f5e8',
                            backgroundColor: reportParams.lancamentoType === type.id ? '#f1f8e9' : '#fafafa',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: '#f1f8e9',
                              borderColor: '#2e7d32'
                            }
                          }}
                        >
                          <FormControlLabel 
                            value={type.id} 
                            control={<Radio color="success" sx={{ display: 'none' }} />} 
                            label={
                              <Box sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                <Icons 
                                  iconName="AttachMoney" 
                                  fontSize={18} 
                                  color={reportParams.lancamentoType === type.id ? '#2e7d32' : '#666'}
                                />
                                <Box sx={{ ml: 1 }}>
                                  <Typography variant="body1" sx={{ 
                                    color: reportParams.lancamentoType === type.id ? '#2e7d32' : '#333',
                                    fontWeight: reportParams.lancamentoType === type.id ? 600 : 400
                                  }}>
                                    {type.name}
                                  </Typography>
                                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                    {type.description}
                                  </Typography>
                                </Box>
                              </Box>
                            } 
                            sx={{ width: '100%', m: 0 }}
                          />
                        </Paper>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Despesas */}
                {despesas.length > 0 && (
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Icons 
                        iconName="TrendingDown" 
                        fontSize={20} 
                        color="#d32f2f"
                      />
                      <Typography variant="subtitle1" sx={{ ml: 1, color: '#d32f2f', fontWeight: 500 }}>
                        Despesas
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {despesas.map((type) => (
                        <Paper 
                          key={type.id}
                          elevation={reportParams.lancamentoType === type.id ? 2 : 0} 
                          sx={{ 
                            p: 2, 
                            borderRadius: '8px',
                            border: reportParams.lancamentoType === type.id ? '2px solid #d32f2f' : '1px solid #ffebee',
                            backgroundColor: reportParams.lancamentoType === type.id ? '#ffebee' : '#fafafa',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: '#ffebee',
                              borderColor: '#d32f2f'
                            }
                          }}
                        >
                          <FormControlLabel 
                            value={type.id} 
                            control={<Radio color="error" sx={{ display: 'none' }} />} 
                            label={
                              <Box sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                <Icons 
                                  iconName="MoneyOff" 
                                  fontSize={18} 
                                  color={reportParams.lancamentoType === type.id ? '#d32f2f' : '#666'}
                                />
                                <Box sx={{ ml: 1 }}>
                                  <Typography variant="body1" sx={{ 
                                    color: reportParams.lancamentoType === type.id ? '#d32f2f' : '#333',
                                    fontWeight: reportParams.lancamentoType === type.id ? 600 : 400
                                  }}>
                                    {type.name}
                                  </Typography>
                                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                    {type.description}
                                  </Typography>
                                </Box>
                              </Box>
                            } 
                            sx={{ width: '100%', m: 0 }}
                          />
                        </Paper>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Outros tipos */}
              {outros.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Icons 
                      iconName="Category" 
                      fontSize={20} 
                      color="#757575"
                    />
                    <Typography variant="subtitle1" sx={{ ml: 1, color: '#757575', fontWeight: 500 }}>
                      Outros
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    display: 'flex', 
                    flexWrap: 'wrap',
                    gap: 2 
                  }}>
                    {outros.map((type) => (
                      <Box sx={{ 
                        flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(33.33% - 12px)' },
                        minWidth: '200px'
                      }} key={type.id}>
                        <Paper 
                          elevation={reportParams.lancamentoType === type.id ? 2 : 0} 
                          sx={{ 
                            p: 2, 
                            borderRadius: '8px',
                            border: reportParams.lancamentoType === type.id ? '2px solid #757575' : '1px solid #f0f0f0',
                            backgroundColor: reportParams.lancamentoType === type.id ? '#f5f5f5' : '#fafafa',
                            transition: 'all 0.2s ease',
                            height: '100%',
                            '&:hover': {
                              backgroundColor: '#f5f5f5',
                              borderColor: '#757575'
                            }
                          }}
                        >
                          <FormControlLabel 
                            value={type.id} 
                            control={<Radio sx={{ display: 'none' }} />} 
                            label={
                              <Box sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                <Icons 
                                  iconName="Assignment" 
                                  fontSize={18} 
                                  color={reportParams.lancamentoType === type.id ? '#757575' : '#999'}
                                />
                                <Box sx={{ ml: 1 }}>
                                  <Typography variant="body1" sx={{ 
                                    color: reportParams.lancamentoType === type.id ? '#757575' : '#333',
                                    fontWeight: reportParams.lancamentoType === type.id ? 600 : 400
                                  }}>
                                    {type.name}
                                  </Typography>
                                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                    {type.description}
                                  </Typography>
                                </Box>
                              </Box>
                            } 
                            sx={{ width: '100%', m: 0 }}
                          />
                        </Paper>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </RadioGroup>
          </FormControl>
        </Paper>
      );

    case 1: // Rubrica
    return (
      <Paper elevation={3} sx={{ p: 3, mt: 2, borderRadius: '8px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Icons 
            iconName="Label" 
            fontSize={24} 
            color="#4aaa9c"
          />
          <Typography variant="h6" sx={{ ml: 1, color: '#4aaa9c', fontWeight: 500 }}>
            Selecione a rubrica
          </Typography>
        </Box>
        
        <SelectGroup
          data={rubricas}
          value={reportParams.rubricaId}
          onChange={onAutocompleteChange}
          placeholder="Rubrica"
          width="100%"
          height={55}
        />
  
        {selectedRubrica && (
          <Box sx={{ 
            mt: 3, 
            p: 2.5, 
            backgroundColor: '#f8fbfa', 
            border: '1px solid #e1f0ef',
            borderRadius: '12px',
            borderLeft: '4px solid #4aaa9c'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Icons 
                iconName="CheckCircle" 
                fontSize={16} 
                color="#4aaa9c"
              />
              <Typography variant="body2" sx={{ 
                color: '#4aaa9c', 
                fontWeight: 600,
                fontSize: '0.8rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                ml: 1
              }}>
                Rubrica selecionada
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ 
              fontWeight: 600,
              fontSize: '1rem',
              color: '#2d5a54',
              mb: 0.5,
              ml: 3
            }}>
              {selectedRubrica.name}
            </Typography>
            <Typography variant="body2" sx={{ 
              color: '#6b7c79',
              fontSize: '0.875rem',
              lineHeight: 1.4,
              ml: 3
            }}>
              {selectedRubrica.description}
            </Typography>
          </Box>
        )}
      </Paper>
    );

    case 2: // Período
      return (
        <Paper elevation={3} sx={{ p: 3, mt: 2, borderRadius: '8px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Icons 
              iconName="DateRange" 
              fontSize={24} 
              color="#4aaa9c"
            />
            <Typography variant="h6" sx={{ ml: 1, color: '#4aaa9c', fontWeight: 500 }}>
              Selecione o período do relatório
            </Typography>
          </Box>
          
          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Checkbox 
                  checked={isFullPeriodChecked}
                  onChange={handleFullPeriodChange}
                  color="primary"
                  sx={{ 
                    color: '#4aaa9c',
                    '&.Mui-checked': {
                      color: '#4aaa9c',
                    },
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Icons 
                    iconName="EventAvailable" 
                    fontSize={18} 
                    color={isFullPeriodChecked ? '#4aaa9c' : '#666'}
                  />
                  <Typography sx={{ fontSize: '0.9rem', ml: 1 }}>
                    Usar período completo do projeto (da data inicial até hoje)
                  </Typography>
                </Box>
              }
            />
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 4
          }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Icons 
                  iconName="Event" 
                  fontSize={16} 
                  color="#666"
                />
                <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>
                  Data inicial
                </Typography>
              </Box>
              <DateComponent
                label=""
                value={reportParams.startDate}
                onChange={(value) => onDateChange('startDate', value)}
                disabled={isFullPeriodChecked}
              />
            </Box>
            
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Icons 
                  iconName="EventBusy" 
                  fontSize={16} 
                  color="#666"
                />
                <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>
                  Data final
                </Typography>
              </Box>
              <DateComponent
                label=""
                value={reportParams.endDate}
                onChange={(value) => onDateChange('endDate', value)}
                disabled={isFullPeriodChecked}
              />
            </Box>
          </Box>
          
          {isFullPeriodChecked && (
            <Box sx={{ 
              mt: 3,
              p: 2, 
              bgcolor: 'rgba(74, 170, 156, 0.1)', 
              borderRadius: '8px',
              border: '1px solid rgba(74, 170, 156, 0.3)',
              display: 'flex',
              alignItems: 'center'
            }}>
              <Icons 
                iconName="Info" 
                fontSize={20} 
                color="#4aaa9c"
              />
              <Typography variant="body2" sx={{ color: '#4aaa9c', ml: 1 }}>
                O relatório incluirá dados desde {project?.initialDate ? new Date(project.initialDate).toLocaleDateString() : 'a data inicial do projeto'} até {new Date().toLocaleDateString()}.
              </Typography>
            </Box>
          )}
        </Paper>
      );

    default:
      return null;
  }
};

export default StepContent;